import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router';
import { loggedIn } from './_redux';
import { useAppSelector, useAppDispatch } from './_redux/hooks';
import { isSessionExpired, logout } from './_redux/_slices/authentication_slice';

interface RequireAuthProps {
  children: React.ReactNode;
}

function RequireAuth({ children }: RequireAuthProps) {
  const isLoggedIn = useAppSelector(loggedIn);
  const expiry = useAppSelector((state) => state.authentication.expiry);
  const location = useLocation();
  const dispatch = useAppDispatch();

  // loadInitialState only checks expiry once, at module load, so a tab left
  // open past the token's expiry keeps `loggedIn` true. Re-check here to catch
  // it on the next guarded navigation instead of rendering pages against
  // credentials the API is going to reject.
  const sessionExpired = isLoggedIn && isSessionExpired(expiry);
  const allowed = isLoggedIn && !sessionExpired;

  useEffect(() => {
    // Dispatching logout, rather than only redirecting, is what clears the
    // persisted session and the in-memory credentials - without it the user
    // lands on /login with the dead token still attached to every request.
    if (sessionExpired) dispatch(logout('Your session has expired, please log back in.'));
  }, [sessionExpired, dispatch]);

  // replace: the guarded URL leaves history, so Back doesn't land on it and
  // bounce straight back to /login.
  return allowed ? <>{children}</> : <Navigate
    replace
    to={{
      pathname: '/login',
      search: location.pathname === '/'
        ? `${location.search}`
        : `?redirect_url=${location.pathname}${location.search.replace('?', '&')}`,
      hash: location.hash,
    }}
    state={{ from: location }}
  />;
}

export default RequireAuth;
